import { useQuery } from '@tanstack/react-query';
import format from 'date-fns/format';
import {
  DateRangeSchema,
  fetchHostAvailabilityByDuration,
} from 'flux/clients/availability';
import { z } from 'zod';

const UseIsHostAvailableByDurationParamsSchema = z.object({
  accountId: z.string().uuid(),
  time: z.string(),
  duration: z.number().int().positive(),
  dateRange: DateRangeSchema,
});

type UseIsHostAvailableByDurationParams = z.infer<
  typeof UseIsHostAvailableByDurationParamsSchema
>;

const useHostAvailabilityByDuration = ({
  accountId,
  time,
  duration, // duration in minutes
  dateRange,
}: UseIsHostAvailableByDurationParams) => {
  const query = useQuery({
    queryKey: ['isHostAvailable', accountId, duration, dateRange.from],
    queryFn: () =>
      fetchHostAvailabilityByDuration(accountId, duration, dateRange),
    retry: 0,
    enabled: !!accountId && !!duration && !!dateRange.from,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const hostIsAvailable =
    !query.isLoading &&
    query.data
      ?.find(
        (v) =>
          format(v.date, 'yyyy-MM-dd') === format(dateRange.from, 'yyyy-MM-dd'),
      ) // @todo: support date range instead of single date
      ?.times?.includes(time);

  return {
    isLoading: query.isLoading,
    availability: query.data,
    hostIsAvailable,
  };
};

export default useHostAvailabilityByDuration;
